import SearchPrediction, { createSearchPredictionModel } from 'data/search-predictions';
import getMap from '@zoocasa/node-kit/map/get-map';
import { ThemeNames } from 'types/themes';
import { loadMapKitCore } from './mapkit-core';

let mapkit: any = null;

interface MapkitPoint {
  coordinate: {
    latitude: number;
    longitude: number;
  };
}

interface MapkitAutocompleteResult {
  query: string;
  results: (MapkitPoint & {
    displayLines: string[];
  })[];
}

type MapkitGeocoderLookupResponse = {
  results: MapkitPoint[];
};

export const transitionZoomLevel = 13;

export async function query(query: string, isCommute = false): Promise<SearchPrediction[]> {
  const mapkitSearch = await loadMapKitSearch();
  return new Promise((resolve, reject) => {
    mapkitSearch.autocomplete(query, (err: Record<string, unknown>, data: MapkitAutocompleteResult) => {
      if (err) {
        return reject(err);
      } else {
        const searchPredictions = filterAndFormatSearchResults(data.results, isCommute).map(createSearchPredictionModel);
        return resolve(searchPredictions);
      }
    });
  });
}

export async function drawInitialMap(mapElementID: string, position: any, isInteractive = false, theme: ThemeNames, renderPin = true) {
  // Ensure MapKit is loaded (will use cached instance if already loaded)
  await loadMapKit();
  
  // Validate element exists
  const mapElement = document.getElementById(mapElementID);
  if (!mapElement) {
    throw new Error(`Map element with ID ${mapElementID} not found`);
  }
  
  // Use the map instance manager to create the map
  return mapInstanceManager.createMap(mapElementID, position, isInteractive, theme, renderPin);
}

// Cleanup function for map instances
export function cleanupMap(mapElementID: string) {
  mapInstanceManager.destroyMap(mapElementID);
}

export async function getDirectionAndAddOverlays(map: any, origin: string, destination: string, isDriving: boolean): Promise<Record<string, number>> {
  await loadMapKit();
  try {
    const transportType = isDriving ? 'driving' : 'walking';
    const directions = await map.directions(destination, origin, transportType);
    if (map.map.overlays.length) {
      map.clearMarkers();
    }
    map.drawOverlays(directions);
    const routeInformation = directions.routes[0];
    const { distance, expectedTravelTime } = routeInformation;
    return { distance, expectedTravelTime };
  } catch {
    return { distance: 0, expectedTravelTime: 0 };
  }
}

export async function getPositionFromAddress(address: string): Promise<MapkitGeocoderLookupResponse> {
  await loadMapKit();
  const geocoder = new mapkit.Geocoder();
  return new Promise((resolve, reject) => {
    geocoder.lookup(address, (err: Record<string, unknown>, data: MapkitGeocoderLookupResponse) => {
      if (err) {
        return reject(err);
      } else {
        return resolve(data);
      }
    });
  });
}

export async function getAddressFromPosition(latitude: number, longitude: number) {
  await loadMapKit();
  const geocoder = new mapkit.Geocoder();
  return new Promise((resolve, reject) => {
    const coordinate = new mapkit.Coordinate(latitude, longitude);
    geocoder.reverseLookup(coordinate, (err: Record<string, unknown>, data: MapkitGeocoderLookupResponse) => {
      if (err) {
        return reject(err);
      } else {
        return resolve(data);
      }
    });
  });
}

export async function loadMapKit() {
  // Use the centralized MapKit loading instead of loading it multiple times
  await loadMapKitCore();
  
  // Set the global mapkit reference for backward compatibility
  if ((window as any)['mapkit'] && !mapkit) {
    mapkit = (window as any)['mapkit'];
  }
}

function filterAndFormatSearchResults(results: MapkitAutocompleteResult['results'], isCommute = false) {
  return results.filter(({ displayLines, coordinate }) => {
    return coordinate && filterForCanadianAreas(displayLines, isCommute);
  }).map(result => {
    const { latitude, longitude } = result.coordinate;
    return {
      id: `${Math.random()}`,
      description: `${result.displayLines.join(', ')}`,
      group: 'locations',
      matchedSubstrings: [],
      data: {
        position: {
          type: 'Point',
          coordinates: [longitude, latitude],
        },
      },
      source: 'apple',
    } as unknown as SearchPrediction;
  });
}

export function filterForCanadianAreas(result: string[], isCommute = false) {
  if (isCommute) {
    return !!(result[1] && result?.[1].match(/^[^\d]+Canada$/));
  }
  return !!(result[1] && result?.[1].match(/^[^\d]+Canada$/) && result?.[0].match(/^([^0-9]*)$/));
}

async function loadMapKitSearch() {
  await loadMapKit();
  const center = new mapkit.Coordinate(43.650848, -79.366969);
  const span = new mapkit.CoordinateSpan(0.001, 0.001);
  const region = new mapkit.CoordinateRegion(center, span);
  return new mapkit.Search({ region, includeAddresses: true, includeQueries: true });
}

// Map Instance Manager for better lifecycle management
class MapInstanceManager {
  private instances = new Map<string, any>();
  
  createMap(elementId: string, position: any, isInteractive: boolean, theme: ThemeNames, renderPin: boolean = true) {
    // Clean up existing instance if it exists
    this.destroyMap(elementId);
    
    const map = getMap('apple') as any;
    const [longitude, latitude] = position.coordinates;
    
    map.drawMap({ 
      latitude: latitude, 
      longitude: longitude, 
      zoom: 14, 
      showsCompass: true, 
      showsZoomControls: true, 
      showsMapTypeControl: false, 
      isScrollEnabled: isInteractive, 
    }, elementId);
    
    // Store the map instance
    this.instances.set(elementId, map);
    
    if (renderPin) {
      if (theme === ThemeNames.ZOOCASA) {
        map.placeImageMarker('/assets/images/current-location-pin.svg', { lat: latitude, lng: longitude });
      } else {
        map.placeImageMarker('/assets/images/current-location-pin-exp.svg', { lat: latitude, lng: longitude });
      }
    }
    
    return map;
  }
  
  destroyMap(elementId: string) {
    if (this.instances.has(elementId)) {
      const map = this.instances.get(elementId);
      try {
        map.removeEventListeners?.();
        map.destroy?.();
      } catch (error) {
        console.warn('Error cleaning up map:', error);
      }
      this.instances.delete(elementId);
    }
    
    // Clear the map element
    const mapElement = document.getElementById(elementId);
    if (mapElement) {
      mapElement.innerHTML = '';
    }
  }
  
  getMap(elementId: string) {
    return this.instances.get(elementId);
  }
  
  hasMap(elementId: string) {
    return this.instances.has(elementId);
  }
}

// Global map instance manager
const mapInstanceManager = new MapInstanceManager();